/**
 * +personas: WoW-style party chat frame (bottom-left) and speech bubbles over the speaker's head.
 * Lines come from the sim's social events (model.ts); the frame fades out after a quiet spell and
 * is fully visible (and scrollable) while hovered or while typing. Enter opens a one-line input
 * that says the line through `say` (offline: the next tick; online: the room). Text is always set
 * with textContent: online it is other players' input.
 */
import { CHAT_MAX_LEN, type UnitId } from '@keystone/sim';
import type { Renderer, RenderUnit } from '../../render/types';
import { el, setClass, setStyle } from '../dom';
import { CHAT_MAX_LINES, type ChatLine, bubbleOpacity, bubbleText, chatOpacity } from './model';

export class ChatFrame {
  readonly root: HTMLElement;
  private readonly log: HTMLElement;
  private readonly input: HTMLInputElement;
  private lastLineAt = -Infinity;
  private hovered = false;
  private typing = false;
  private canType = false;
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.code !== 'Enter' && e.code !== 'NumpadEnter') return;
    if (this.typing || !this.canType) return;
    const t = e.target as HTMLElement | null;
    // Another text field (settings) has the keyboard.
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    e.preventDefault();
    this.open();
  };

  constructor(parent: HTMLElement, private readonly say?: (text: string) => void) {
    this.root = el('div', 'chat-frame');
    parent.appendChild(this.root);
    this.log = el('div', 'chat-log', this.root);
    this.input = el('input', 'chat-input', this.root);
    this.input.type = 'text';
    this.input.maxLength = CHAT_MAX_LEN;
    this.input.placeholder = 'Party: say something (Enter)';
    this.input.spellcheck = false;
    this.input.autocomplete = 'off';
    this.root.addEventListener('mouseenter', () => (this.hovered = true));
    this.root.addEventListener('mouseleave', () => (this.hovered = false));
    this.input.addEventListener('keydown', (e) => {
      // Keys typed here never reach the game (input/dom.ts skips text fields); stop Enter/Escape here.
      if (e.code === 'Enter' || e.code === 'NumpadEnter') {
        e.preventDefault();
        const text = this.input.value.trim();
        if (text) this.say?.(text);
        this.close();
      } else if (e.code === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.close();
      }
    });
    this.input.addEventListener('blur', () => this.close());
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.onKey);
  }

  add(lines: readonly ChatLine[], now: number): void {
    if (lines.length === 0) return;
    const stick = this.log.scrollTop + this.log.clientHeight >= this.log.scrollHeight - 4;
    for (const l of lines) {
      const row = el('div', `chat-line ${l.kind}`, this.log);
      if (l.channel) el('span', 'chat-channel', row, `[${l.channel}] `); // +presence: zone chat
      if (l.kind === 'say') {
        const n = el('span', 'chat-name', row, `[${l.name}]`);
        n.style.color = l.color;
        row.append(`: ${l.text}`);
      } else if (l.kind === 'emote') {
        const n = el('span', 'chat-name', row, l.name);
        n.style.color = l.color;
        row.append(` ${l.text}`);
      } else row.textContent = l.text;
    }
    while (this.log.childElementCount > CHAT_MAX_LINES) this.log.firstElementChild?.remove();
    // Follow new lines unless the player scrolled up to read.
    if (stick || !this.hovered) this.log.scrollTop = this.log.scrollHeight;
    this.lastLineAt = now;
  }

  /** +presence: the input's channel label ('Zone' in a shared field; null = party). */
  setChannel(label: string | null): void {
    const ph = label ? `${label}: say something (Enter)` : 'Party: say something (Enter)';
    if (this.input.placeholder !== ph) this.input.placeholder = ph;
  }

  update(now: number, canType: boolean): void {
    this.canType = canType && !!this.say;
    if (!this.canType && this.typing) this.close();
    setStyle(this.root, 'opacity', chatOpacity(now, this.lastLineAt, this.hovered || this.typing).toFixed(2));
    setClass(this.root, 'active', this.hovered || this.typing);
  }

  private open(): void {
    this.typing = true;
    setClass(this.root, 'typing', true);
    this.input.value = '';
    this.input.focus();
  }

  private close(): void {
    if (!this.typing) return;
    this.typing = false;
    setClass(this.root, 'typing', false);
    this.input.value = '';
    this.input.blur();
  }

  dispose(): void {
    if (typeof window !== 'undefined') window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }
}

interface Bubble {
  root: HTMLElement;
  since: number;
  x: number;
  y: number;
}

/** One speech bubble per unit (a newer line replaces the older one). */
export class SpeechBubbles {
  private readonly layer: HTMLElement;
  private readonly bubbles = new Map<UnitId, Bubble>();

  constructor(parent: HTMLElement) {
    this.layer = el('div', 'chat-bubbles', parent);
  }

  show(unitId: UnitId, text: string, emote: boolean, now: number): void {
    let b = this.bubbles.get(unitId);
    if (!b) {
      b = { root: el('div', 'chat-bubble', this.layer), since: now, x: NaN, y: NaN };
      el('div', 'chat-bubble-body', b.root);
      this.bubbles.set(unitId, b);
    }
    b.since = now;
    const body = b.root.firstElementChild as HTMLElement;
    body.textContent = bubbleText(text);
    setClass(b.root, 'emote', emote);
  }

  update(now: number, units: ReadonlyMap<UnitId, RenderUnit>, renderer: Renderer): void {
    for (const [id, b] of this.bubbles) {
      const ru = units.get(id);
      const op = bubbleOpacity(now, b.since);
      if (op <= 0 || !ru) {
        b.root.remove();
        this.bubbles.delete(id);
        continue;
      }
      const sp = renderer.project(ru.pos, renderer.unitHeight(id) + 0.5);
      setClass(b.root, 'off', !sp.visible);
      if (!sp.visible) continue;
      const x = Math.round(sp.x);
      const y = Math.round(sp.y);
      if (x !== b.x || y !== b.y) {
        b.x = x;
        b.y = y;
        b.root.style.transform = `translate3d(${x}px,${y}px,0)`;
      }
      setStyle(b.root, 'opacity', op.toFixed(2));
    }
  }

  dispose(): void {
    this.layer.remove();
  }
}
